import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { usePermission } from '../../../../auth/AuthContext';
import { personnelApi } from '../personnelApi';
import type { PersonnelCatalogEntry, PlantillaVacantItem } from '../personnelTypes';
import { sampleServiceRecord } from '../utils/sampleServiceRecord';
import { vacantPositionOptions, vacantSlotOptions } from '../utils/vacantPositionOptions';

/**
 * Records a line of service — which post this employee occupies, from when.
 *
 * **It RECORDS rather than appoints, and the wording is the change.** Product call,
 * 15 Sep 2026. "Appoint" names a decision somebody else made in a document this system does not
 * hold; what happens here is a clerk writing down where a person is posted. The distinction
 * matters because the form carries no approval, no order number and no authority — presenting
 * it as the act of appointing claimed a weight it does not have.
 *
 * **The fields mirror the create form's Employment step**, because they are the same question:
 * which post, which slot, from when, on what paper. A reader who filled the wizard finds the
 * same pickers in the same order.
 *
 * **One field is here that the wizard does not offer: adding a nature of appointment in
 * place.** That step is already five pickers deep and a reader creating an employee is not the
 * reader curating a vocabulary; a reader recording a promotion is exactly who meets a word the
 * catalog is missing.
 *
 * **Several open service records are allowed**, and exactly one of them is primary — see the
 * checkbox below for what that decides.
 */
export function ServiceRecordModal({
  personnelId,
  hasOpenServiceRecord,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Drives whether primary is a question at all — the first service record is always it. */
  hasOpenServiceRecord: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [vacant, setVacant] = useState<PlantillaVacantItem[]>([]);
  const [positionId, setPositionId] = useState<string | null>(null);

  /*
    The SLOT, chosen by the reader rather than allocated by the server. The request carries
    this and not the position: the position picker narrows the list below it, and the row is
    written against an item.
  */
  const [itemId, setItemId] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState('');

  /*
    The appointment behind the line of service. All three are optional: a record can be filed
    before the instrument is in hand, and a required field the reader cannot answer is a form
    they cannot submit.
  */
  const [appointmentDate, setAppointmentDate] = useState('');
  const [assumptionDate, setAssumptionDate] = useState('');
  const [appointmentNatureId, setAppointmentNatureId] = useState<string | null>(null);
  const [natures, setNatures] = useState<PersonnelCatalogEntry[]>([]);
  const [isPrimary, setIsPrimary] = useState(!hasOpenServiceRecord);
  const [remarks, setRemarks] = useState('');

  /*
    The catalog's OWN key, not this form's. Adding a word every future service record can be
    filed under is a different grant from recording that one employee moved — so the Add row is
    withheld from a reader without it rather than offered and 403'd.
  */
  const canAddNature = usePermission('personnel.manage-catalogs');

  const [loadingVacant, setLoadingVacant] = useState(true);
  const [loadingNatures, setLoadingNatures] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    personnelApi
      .vacantItems(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setVacant(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load the plantilla.', 0),
        );

        // No focus call: a failed load names no field.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingVacant(false);
      });

    return () => controller.abort();
  }, []);

  /*
    The nature catalog, in its own effect and its own loading flag. A failure here must not
    blank the position picker: the two are independent feeds, and a form that cannot offer a
    vocabulary can still record where somebody is posted.
  */
  useEffect(() => {
    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setNatures(next.appointmentNatures);
      })
      .catch(() => {
        // No banner and no focus call: a failed catalog load names no field, and the field it
        // fills is optional. The picker renders empty and the rest of the form still saves.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingNatures(false);
      });

    return () => controller.abort();
  }, []);

  /*
    Checked here so the reader is told before submitting, and on the server so it holds for any
    caller. Nobody assumes a post before it is given to them.
  */
  const assumedBeforeAppointed =
    appointmentDate !== '' && assumptionDate !== '' && assumptionDate < appointmentDate;

  const valid = itemId !== null && effectiveFrom.length > 0 && !assumedBeforeAppointed;
  const canSave = valid && confirmed && !saving;

  function generate() {
    const sample = sampleServiceRecord(vacant, natures);

    if (!sample) return;

    setPositionId(sample.plantillaPositionId);
    setItemId(sample.plantillaItemId);
    setEffectiveFrom(sample.effectiveFrom);
    setAppointmentDate(sample.appointmentDate);
    setAssumptionDate(sample.assumptionDate);

    // A real row out of the catalog the form already loaded — an invented id fails the
    // server's "it must exist and be active" check, which reads as a bug in the form.
    setAppointmentNatureId(sample.appointmentNatureId);
    setRemarks(sample.remarks);
  }

  /**
   * Writes the catalog row, then selects the id it comes back with.
   *
   * The picker is over a FOREIGN KEY, so committing the typed text — what a bare `allowCustom`
   * does — would put a label where an id belongs. The create is idempotent on the name, so two
   * readers adding "Reemployment" at once get one row and no error.
   */
  async function createNature(raw: string) {
    const name = raw.trim();
    if (!name) return;

    try {
      const created = await personnelApi.addAppointmentNature(name);

      setNatures((current) =>
        current.some((n) => n.id === created.id) ? current : [...current, created],
      );
      setAppointmentNatureId(created.id);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not add that nature of appointment.', 0),
      );
    }
  }

  async function save() {
    if (!canSave || itemId === null) return;

    setSaving(true);
    setError(null);

    try {
      await personnelApi.addServiceRecord(personnelId, {
        /*
          The exact slot the reader chose. The list it came from is a moment old, so the server
          re-checks vacancy inside the write — a slot taken in between comes back as a rejection
          rather than a double booking.
        */
        plantillaItemId: itemId,
        effectiveFrom,

        // Empty is ABSENT, not a blank date: the server distinguishes "not recorded" from a
        // value, and "" would be neither.
        appointmentDate: appointmentDate || null,
        assumptionDate: assumptionDate || null,
        appointmentNatureId,
        isPrimary,
        remarks: remarks.trim() || null,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not record the service record.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'service-record' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add service record"
      size="lg"
      id="service-record-modal"
      autogenerate={{
        id: 'service-record-autogenerate',
        onGenerate: generate,
        disabled: saving || loadingVacant || vacant.length === 0,
      }}
      confirm={{
        id: 'service-record-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="service-record-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="service-record-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="service-record-plantilla-position-id"
          label="Position"
          required
          description="Vacant positions only, across every unit."
          value={positionId}
          onChange={(next) => {
            setPositionId(next);

            // The slot belongs to the position; keeping it across a change would post a seat
            // in a job the reader just moved off.
            setItemId(null);
          }}
          options={vacantPositionOptions(vacant)}
          sort="none"
          loading={loadingVacant}
          error={error?.fieldError('plantillaPositionId')}
          disabled={saving}
        />

        {/*
          The SLOT, after the position and narrowed to it — the same pair the create form asks,
          in the same order.

          **Always DRAWN, and disabled until a position is chosen.** Product call, 15 Sep 2026:
          it used to render only once a position was picked, so a required field appeared out of
          nowhere and everything below it jumped down the form. Disabled for STATE, never
          hidden — hiding is what a permission gate does, and this is not one.

          It says WHY through its own `description`, which is the shared input's explanation
          channel and carries the `sr-only` twin with it. `<Select>` has no `disabledReason`
          prop, and inventing one on a component with thirty callers to say a sentence the
          description already has a place for would be the wrong layer to fix this at.
        */}
        <Select
          id="service-record-plantilla-item-id"
          label="Plantilla slot"
          required
          description={
            positionId === null
              ? 'Choose a position first — the slots belong to it.'
              : 'The item number this service record is written against.'
          }
          value={itemId}
          onChange={setItemId}
          options={vacantSlotOptions(vacant, positionId)}
          sort="none"
          error={error?.fieldError('plantillaItemId')}
          disabled={saving || positionId === null}
        />

        {!loadingVacant && vacant.length === 0 ? (
          <p className="text-xs text-slate-500">
            No vacant positions in the plantilla. Add one there before recording a service record.
          </p>
        ) : null}

        {/*
          THREE dates, and they are not the same fact: the effectivity is the period this row
          records, the appointment is the paper somebody signed, and the assumption is the day
          they reported. They routinely differ by weeks.

          ONE grid, because a date field never takes a row of its own in a modal — `mm/dd/yyyy`
          plus a calendar button is a fixed, small amount of content, and a date stretched across
          both columns is a box mostly full of nothing. The third wraps onto its own row, which
          reads as the end of the run rather than as a stranded field.

          NEITHER of the two below is capped at today, unlike a hire date. An instrument dated
          ahead of the assumption is normal, and a future-date guard copied from that field would
          refuse a paper the reader is holding.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="service-record-effective-from"
            label="Effective from"
            required
            description="The day this service record takes effect — not the employee's hire date."
            value={effectiveFrom}
            onChange={setEffectiveFrom}
            error={
              effectiveFrom ? error?.fieldError('effectiveFrom') : 'Choose when it takes effect.'
            }
            disabled={saving}
          />

          <DateField
            id="service-record-appointment-date"
            label="Appointment date"
            description="The day the appointment instrument is dated."
            value={appointmentDate}
            onChange={setAppointmentDate}
            error={error?.fieldError('appointmentDate')}
            disabled={saving}
          />

          <DateField
            id="service-record-assumption-date"
            label="Assumption date"
            description="The day the employee assumed the duties of the post."
            value={assumptionDate}
            /*
              The client copy of the server's rule, so the reader is told before submitting
              rather than after. The server's is the one that holds.
            */
            min={appointmentDate || undefined}
            onChange={setAssumptionDate}
            error={
              assumedBeforeAppointed
                ? 'Choose a date on or after the appointment date.'
                : error?.fieldError('assumptionDate')
            }
            disabled={saving}
          />
        </div>

        {/*
          The nature of the appointment — WHY the movement happened. Not the employment type,
          which is the STATUS of the appointment and lives on the employee's own record: one
          person can be permanently appointed by promotion, and both facts are on the form.

          `allowCustom` with `onCreateOption`: an unmatched query offers Add "…" and the handler
          writes the catalog row under its own key before selecting it. Withheld from a reader
          without that key — hide, don't disable — and the sentence about adding one goes with
          it rather than promising an Add row they will never be shown.
        */}
        <Select
          id="service-record-appointment-nature-id"
          label="Nature of appointment"
          loading={loadingNatures}
          description={
            canAddNature
              ? 'Why the movement happened — original appointment, promotion, transfer, reassignment. Type a value that is not listed to add it.'
              : 'Why the movement happened — original appointment, promotion, transfer, reassignment.'
          }
          allowCustom={canAddNature}
          onCreateOption={canAddNature ? createNature : undefined}
          value={appointmentNatureId}
          onChange={(next) => setAppointmentNatureId(next || null)}
          options={natures.map((n) => ({ value: n.id, label: n.name }))}
          sort="none"
          error={error?.fieldError('appointmentNatureId')}
          disabled={saving}
        />

        {/*
          Only asked once there is something for it to be primary over. A first service record is
          always primary, so offering the choice would be a control with one valid answer.

          The consequence is on the FORM, before the tick — never appended to it.
        */}
        {hasOpenServiceRecord ? (
          <label
            htmlFor="service-record-primary"
            className="flex items-start gap-2 text-sm text-slate-600"
          >
            <Checkbox
              id="service-record-primary"
              name="service-record-primary"
              checked={isPrimary}
              onChange={() => setIsPrimary((v) => !v)}
              disabled={saving}
            />
            <span>
              Make this their primary service record.
              <span className="block text-xs text-slate-500">
                An employee may hold several posts at once, and exactly one of them is primary.
                The primary one is what the record reads its grade, unit and reporting line from,
                and it is the salary grade payroll will compute against. The post currently
                marked primary becomes secondary.
              </span>
            </span>
          </label>
        ) : null}

        <TextField
          id="service-record-remarks"
          label="Remarks"
          description="Why the service record was recorded, when it needs saying."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          error={error?.fieldError('remarks')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
